import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ServiceAccountCard, ServiceAccountFormData, Utility } from '@model';
import { ReferenceService } from '@services';
import { CookieService } from '../../shared/services/cookie.service';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { map, Observable, startWith, Subscription } from 'rxjs';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';
import { ECRMErrorStateMatcher } from 'src/app/shared/matchers/ecrm-error.state-matcher';

enum Status {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
  RETIRED = 'RETIRED',
}

@Component({
  selector: 'app-service-accounts',
  templateUrl: './service-accounts.component.html',
  styleUrls: ['./service-accounts.component.scss'],
  providers: [{ provide: DateAdapter, useClass: NativeDateAdapter }],
})
export class ServiceAccountsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() mode: string;
  @Input() index: number = 0;
  @Input() serviceAccount!: ServiceAccountCard;
  @Input() hasServerError: boolean = false;
  panelOpenedState: boolean = false;

  @Output() remove = new EventEmitter<number>();
  @Output() formChange = new EventEmitter<{ index: number; form: FormGroup }>();
  @Output() onClickEdit = new EventEmitter<number>();

  displayUtility = (u: Utility | null) => (u ? u.displayLabel : '');

  public serviceAccountForm: FormGroup;
  private subscription: Subscription;
  utilities: Utility[] = [];
  filteredUtilities: Observable<Utility[]>;

  ecrmErrorMatcher = new ECRMErrorStateMatcher();

  dateFilter = (d: Date | null): boolean => {
    const today = new Date();
    return d <= today;
  };

  constructor(
    private cookieService: CookieService,
    private formBuilder: FormBuilder,
    private referenceService: ReferenceService,
  ) {
    this.serviceAccountForm = this.createForm();
  }

  get locale() {
    return this.cookieService.getLocale();
  }

  ngOnInit(): void {
    this.panelOpenedState = this.serviceAccount.expanded;
    this.serviceAccountForm.patchValue({
      serviceAccountNumber: this.serviceAccount.serviceAccountNumber,
      status: this.serviceAccount.status,
      ecrmId: this.serviceAccount.ecrmId,
      utility: this.utilities.find((u) => u.name === this.serviceAccount.utilityName) || null,
      startDate: this.serviceAccount.startDate,
      endDate: this.serviceAccount.endDate,
    });

    this.serviceAccountForm.get('status').disable();

    this.subscription = this.serviceAccountForm.valueChanges.subscribe((value) => {
      this.serviceAccount.serviceAccountNumber = value.serviceAccountNumber || '';
      this.serviceAccount.status = this.serviceAccountForm.getRawValue().status || Status.INACTIVE;
      this.serviceAccount.ecrmId = value.ecrmId || '';
      this.serviceAccount.utilityName = value.utility ? value.utility.name : '';
      this.serviceAccount.startDate = value.startDate || null;
      this.serviceAccount.endDate = value.endDate || null;

      this.formChange.emit({
        index: this.index,
        form: this.serviceAccountForm,
      });
    });

    this.referenceService.utilities$.subscribe((utilities) => {
      if (utilities && utilities.length > 0) {
        this.utilities = utilities;
        const utility = this.utilities.find((u) => u.name === this.serviceAccount.utilityName) || null;
        this.serviceAccountForm.get('utility')?.setValue(utility);
      }
    });

    this.formChange.emit({
      index: this.index,
      form: this.serviceAccountForm,
    });

    const ctrl = this.serviceAccountForm.get('utility')!;

    this.filteredUtilities = ctrl.valueChanges.pipe(
      startWith(ctrl.value), // seed with current
      map((val) => this._toQuery(val)), // string to search
      map((q) => (q ? this._filterUtilities(q) : this.utilities.slice())),
    );

    this.serviceAccountForm.get('startDate')?.valueChanges.subscribe(() => {
      this.serviceAccountForm.get('endDate')?.updateValueAndValidity({ onlySelf: true });
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['index'] && !changes['index'].firstChange) {
      this.formChange.emit({
        index: this.index,
        form: this.serviceAccountForm,
      });
    }
  }

  private createForm(): FormGroup {
    return this.formBuilder.group({
      status: ['', Validators.required],
      serviceAccountNumber: ['', Validators.required],
      startDate: [null, Validators.required],
      endDate: [null, [this.endDateBeforeStartDateValidator()]],
      utility: ['', Validators.required],
      ecrmId: ['', [this.ecrmValidator()]],
    });
  }

  public onToggle(): void {
    this.onClickEdit.emit(this.index);
  }

  public onRemove(event: Event): void {
    event.stopPropagation();
    this.remove.emit(this.index);
  }

  public getFormData(): ServiceAccountFormData {
    return this.serviceAccountForm.value as ServiceAccountFormData;
  }

  public isValid(): boolean {
    return this.serviceAccountForm.valid;
  }

  public markAllAsTouched(): void {
    this.serviceAccountForm.markAllAsTouched();
  }

  ecrmValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      const ecrmPattern = /^[A-Za-z0-9]{18}$/;
      if (!ecrmPattern.test(value)) {
        return { invalidEcrm: true };
      }

      return null;
    };
  }

  endDateBeforeStartDateValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as Date | null;
      if (!value) {
        return null;
      }
      const start = this.serviceAccountForm.get('startDate')?.value as Date | null;

      return value < start ? { endBeforeStart: true } : null;
    };
  }

  getUtilityLabel() {
    return (
      this.utilities?.find((u) => u.name === this.serviceAccount.utilityName)?.displayLabel ||
      this.serviceAccount.utilityName
    );
  }

  getStatusDisplayLabel(): string {
    return this.serviceAccount.status === 'ACTIVE'
      ? 'utility.account.statuses.active'
      : 'utility.account.statuses.inactive';
  }

  getStartDateLabel(): string {
    if (this.serviceAccount.startDate) {
      const locale = this.locale.replace('_', '-');
      return new Date(this.serviceAccount.startDate).toLocaleDateString(locale, { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
    return '-';
  }
  getEndDateLabel(): string {
    if (this.serviceAccount.endDate) {
      const locale = this.locale.replace('_', '-');
      return new Date(this.serviceAccount.endDate).toLocaleDateString(locale, { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
    return '-';
  }
  getEcrmIdLabel(): string {
    return this.serviceAccount.ecrmId || '-';
  }

  expandPanel() {
    this.panelOpenedState = true;
  }

  onDateChange() {
    this.serviceAccountForm.get('endDate')?.updateValueAndValidity({ onlySelf: true });
    const today = new Date();
    const startDate = this.serviceAccountForm.get('startDate')?.value;
    const endDate = this.serviceAccountForm.get('endDate')?.value;

    if ((startDate && startDate > today) || (endDate && endDate < today)) {
      this.serviceAccountForm.get('status')?.setValue(Status.INACTIVE);
    } else {
      this.serviceAccountForm.get('status')?.setValue(Status.ACTIVE);
    }
  }

  private _toQuery(val: string | Utility | null): string {
    if (!val) return '';
    return typeof val === 'string' ? val : val.displayLabel ?? val.name;
  }

  private _filterUtilities(query: string): Utility[] {
    const q = query.trim().toLowerCase();
    return this.utilities.filter((u) => (u.displayLabel ?? u.name).toLowerCase().includes(q));
  }
}
